import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import API from '../services/api';
import { isPremium as checkIsPremium } from '../utils/premium';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isPremium, setIsPremium] = useState(false); // ✅ State ajouté

  // Configuration axios avec le token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchProfile();
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }
  }, [token]);

  // Écouter les refreshs silencieux de token
  useEffect(() => {
    const handleTokenRefresh = (e) => {
      const newToken = e.detail.token;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    };
    window.addEventListener('tokenRefreshed', handleTokenRefresh);
    return () => window.removeEventListener('tokenRefreshed', handleTokenRefresh);
  }, []);

  const fetchProfile = async () => {
    try {
      // Via apiClient (et non axios global) : bénéficie du silent-refresh sur 401.
      // Au reload avec un access token expiré (15 min), le refresh se fait tout seul
      // au lieu de déconnecter l'utilisateur.
      const response = await API.utilisateurs.getProfile();
      const userData = response.utilisateur || response.data?.utilisateur || response;
      setUser(userData);
      setIsPremium(checkIsPremium(userData));
    } catch (error) {
      // Ne déconnecter que sur 401 définitif (refresh échoué). Sur erreur réseau/5xx,
      // on garde la session : le cookie refresh reste valide.
      if (error?.status === 401 || error?.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const updateUserStatus = async () => {
    try {
      const response = await API.utilisateurs.getProfile();
      const userData = response.utilisateur || response.data?.utilisateur || response;
      setUser(userData);
      setIsPremium(checkIsPremium(userData));
    } catch (error) {
      // silently fail — le profil sera mis a jour au prochain refresh
    }
  };

  const login = async (email, motDePasse) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        motDePasse
      }, {
        withCredentials: true // ✅ Permet les cookies
      });
      // Gérer la structure de réponse du backend
      const { token, utilisateur } = response.data.data || response.data;
      localStorage.setItem('token', token);
      setToken(token);
      setUser(utilisateur);
      // ✅ Mettre à jour isPremium lors du login
      setIsPremium(checkIsPremium(utilisateur));
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur de connexion'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData, { withCredentials: true });
      // Gérer la structure de réponse du backend
      const { token, utilisateur } = response.data.data || response.data;
      localStorage.setItem('token', token);
      setToken(token);
      setUser(utilisateur);
      // ✅ Mettre à jour isPremium lors de l'inscription
      setIsPremium(checkIsPremium(utilisateur));
      return { success: true };
    } catch (error) {
      const data = error.response?.data;
      return {
        success: false,
        message: data?.message || 'Erreur lors de l\'inscription',
        errors: data?.errors || null
      };
    }
  };

  const socialLogin = async (provider, socialToken, extraData = {}) => {
    try {
      const response = await axios.post('/api/auth/social-login', {
        provider,
        token: socialToken,
        ...extraData
      }, { withCredentials: true });
      const { token, utilisateur } = response.data.data || response.data;
      localStorage.setItem('token', token);
      setToken(token);
      setUser(utilisateur);
      setIsPremium(checkIsPremium(utilisateur));
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur de connexion sociale'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsPremium(false); // ✅ Reset isPremium lors du logout
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    // ✅ Mettre à jour isPremium quand l'utilisateur est modifié
    setIsPremium(checkIsPremium(updatedUser));
  };

  const value = {
    user,
    loading,
    login,
    register,
    socialLogin,
    logout,
    updateUser,
    updateUserStatus,
    token,
    isAuthenticated: !!user,
    isParent: user?.role === 'parent',
    isMembre: user?.role === 'membre',
    isAdmin: user?.role === 'admin',
    isPremium
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};